import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PollCreator from "@core/components/PollCreator";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { parseNewPollParams, toNewPollInitialData } from "@core/lib/ai-poll";
import { SEOHead } from "../components/SEOHead";
import { CtaTracker } from "@core/services/CtaTracker";

/**
 * Lien pré-rempli `/new` (spec §3.4) :
 *   /new?title=…&dates=YYYY-MM-DD,YYYY-MM-DD&slots=HH:MM-HH:MM
 *
 * Rend `PollCreator` DIRECTEMENT (pas via /create ni le workspace IA, qui
 * auto-sauvegardent le sondage courant) : rien n'est créé à l'ouverture ni
 * au rechargement. Le visiteur relit, corrige, puis publie lui-même.
 * Le « Magic Link » d'EditorStateProvider exclut /new pour la même raison.
 */
export default function NewPollFromLink() {
  const { t } = useTranslation(["polls", "ai_poll"]);
  const location = useLocation();
  const navigate = useNavigate();

  const { initialData, warnings } = useMemo(() => {
    const parsed = parseNewPollParams(location.search);
    return {
      initialData: toNewPollInitialData(parsed.validation.value),
      warnings: [...parsed.warnings, ...parsed.validation.errors],
    };
  }, [location.search]);

  // A7 : ouverture de la création, avec sa provenance (?ref=) si présente.
  useEffect(() => {
    CtaTracker.trackCreateStarted("new", location.search);
  }, [location.search]);

  return (
    <div className="min-h-screen-safe bg-background text-foreground">
      <SEOHead title={t("polls:new_link.meta_title")} noIndex />
      <ProductHeader productType="date" showAuth={true} />
      <div className="max-w-4xl mx-auto px-4 pt-20">
        <div
          role="status"
          data-testid="new-link-banner"
          className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm"
        >
          <p className="font-medium">{t("polls:new_link.banner")}</p>
          {warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-muted-foreground">
              {warnings.map((w, i) => (
                <li key={`${w.code}-${i}`}>
                  {t(`ai_poll:${w.messageKey}`, {
                    ...w.params,
                    defaultValue: w.message,
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <PollCreator
        initialData={initialData as never}
        onFinalize={(_draft, poll) => {
          const id = poll?.slug || poll?.id;
          if (id) navigate(`/poll/${id}/results`);
        }}
      />
    </div>
  );
}
